import { useMemo } from 'react'
import { DogApplication } from '@/types'

interface ApplicationPricingParams {
  application: DogApplication
  currentDiscount?: { amount: number } | null
}

interface ApplicationPricing {
  transactionFees: string
  total: string
  reminder: string
  essentials: string
}

/**
 * Custom hook to calculate all pricing details for a dog application
 * including Stripe fees on add-ons and shipping
 */
export const usePricing = ({
  application,
  currentDiscount,
}: ApplicationPricingParams): ApplicationPricing => {
  const pricing = useMemo(() => {
    const STRIPE_PERCENT = 2.9
    const STRIPE_FIXED = 30

    const dogPrice = application?.dog?.salePrice?.subTotal || 0
    const shipping = application?.proposedShippingPrice || 0
    const brilliantPadPrice = application?.brilliantPadPrice || 0
    const starterKitPrice = application?.starterKit || 0
    const discount = currentDiscount?.amount || 0
    const deposit = application?.dog?.salePrice?.deposit || 0

    // Calculate the subtotal with all items (before any Stripe fees)
    const grandTotal =
      dogPrice + shipping + brilliantPadPrice + starterKitPrice - discount

    // Calculate Stripe fee on the entire subtotal: (subtotal × 2.9%)
    // Note: Fixed fee ($0.30) is already included in the base puppy price
    const totalStripeFee = Math.round((grandTotal * STRIPE_PERCENT) / 100)

    // Calculate totals
    const total = grandTotal + totalStripeFee
    const reminder = grandTotal - deposit + totalStripeFee

    // Check if we have a shipping range scenario
    const hasShippingRange =
      application?.maxEstimatedShippingPrice &&
      application?.minEstimatedShippingPrice &&
      !application?.shippingEstimate &&
      !application?.proposedShippingPrice

    // Format pricing based on shipping status
    let totalFormatted: string
    let reminderFormatted: string
    let transactionFeesFormatted: string

    if (hasShippingRange) {
      // Calculate once for shipping range scenario
      const minSubtotal =
        dogPrice +
        application.minEstimatedShippingPrice +
        brilliantPadPrice +
        starterKitPrice -
        discount
      const maxSubtotal =
        dogPrice +
        application.maxEstimatedShippingPrice +
        brilliantPadPrice +
        starterKitPrice -
        discount

      const minStripeFee =
        Math.round((minSubtotal * STRIPE_PERCENT) / 100) + STRIPE_FIXED
      const maxStripeFee =
        Math.round((maxSubtotal * STRIPE_PERCENT) / 100) + STRIPE_FIXED

      const minTotal = minSubtotal + minStripeFee
      const maxTotal = maxSubtotal + maxStripeFee

      const minReminder = minSubtotal - deposit + minStripeFee
      const maxReminder = maxSubtotal - deposit + maxStripeFee

      // Format all values as ranges
      totalFormatted = `$${(minTotal / 100).toFixed(2)} - $${(maxTotal / 100).toFixed(2)}`
      reminderFormatted = `$${(minReminder / 100).toFixed(2)} - $${(maxReminder / 100).toFixed(2)}`

      // Transaction fees without fixed fee for display
      const minFeeDisplay = Math.round((minSubtotal * STRIPE_PERCENT) / 100)
      const maxFeeDisplay = Math.round((maxSubtotal * STRIPE_PERCENT) / 100)
      transactionFeesFormatted = `$${(minFeeDisplay / 100).toFixed(2)} - $${(maxFeeDisplay / 100).toFixed(2)}`
    } else {
      // Calculated values for all other cases (including pickup)
      totalFormatted = `$${(total / 100).toFixed(2)}`
      reminderFormatted = `$${(reminder / 100).toFixed(2)}`
      transactionFeesFormatted = `$${(totalStripeFee / 100).toFixed(2)}`
    }

    // Calculate essentials (add-ons) total (Brilliant Pad + Starter Kit)
    const essentialsFormatted =
      Number((starterKitPrice + brilliantPadPrice) / 100).toFixed(2) || '0.00'

    // console.table({
    //   dogPrice,
    //   shipping,
    //   brilliantPadPrice,
    //   starterKitPrice,
    //   discount,
    //   grandTotal,
    //   totalStripeFee,
    // })

    return {
      transactionFees: transactionFeesFormatted,
      total: totalFormatted,
      reminder: reminderFormatted,
      essentials: essentialsFormatted,
    }
  }, [application, currentDiscount])

  return pricing
}
